/** Browser facts used for platform decisions (injected for tests). */
export interface PlatformFacts {
  /** `navigator.userAgent`. */
  readonly userAgent: string;
  /** `navigator.maxTouchPoints`. */
  readonly maxTouchPoints: number;
  /** Whether the page runs as installed app (`display-mode: standalone` or iOS `standalone`). */
  readonly standalone: boolean;
}

/** Operating system families that need different installation instructions. */
export type PlatformKind = "ios" | "android" | "desktop";

/**
 * Classifies the platform. iPadOS reports a desktop Safari user agent; it is recognized by touch
 * support.
 *
 * @param facts - Browser facts.
 * @returns The platform.
 */
export function detectPlatform(facts: PlatformFacts): PlatformKind {
  const agent = facts.userAgent;
  const ios =
    /iPhone|iPad|iPod/u.test(agent) || (agent.includes("Macintosh") && facts.maxTouchPoints > 1);
  let kind: PlatformKind = "desktop";

  if (ios) {
    kind = "ios";
  } else if (agent.includes("Android")) {
    kind = "android";
  }

  return kind;
}

/**
 * Checks whether Web Push needs an installed app: iOS offers it only to web apps added to the home
 * screen.
 *
 * @param facts - Browser facts.
 * @returns `true` on iOS in a browser tab.
 */
export function pushRequiresInstallation(facts: PlatformFacts): boolean {
  const required = detectPlatform(facts) === "ios" && !facts.standalone;

  return required;
}
